import { useState, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { MemberMultiSelectDropdown, ProjectListDropdown } from '../common';

const CopyScheduleModal = ({
  showCopySchedules,
  handleShowCopySchedules,
  handleCopySchedules,
  selectedWeek,
  projectsList,
  selectedProject,
  handleProjectChange,
  teamMembersList,
}) => {
  const selectedTeamMembers = [];
  const sortedMembersList = teamMembersList.filter(function (el) {
    return el.status !== 'inactive';
  });
  const selectCopyMembersInputRef = useRef(null);
  const [copyFromStartDate, setCopyFromStartDate] = useState('');
  const [copyFromEndDate, setCopyFromEndDate] = useState('');
  const [copyToStartDate, setCopyToStartDate] = useState('');
  const [copyToEndDate, setCopyToEndDate] = useState('');

  const handleFromDateChange = (event, picker) => {
    setCopyFromStartDate(moment(picker.startDate));
    setCopyFromEndDate(moment(picker.endDate));
  };

  const handleToDateChange = (event, picker) => {
    setCopyToStartDate(moment(picker.startDate));
    setCopyToEndDate(moment(picker.endDate));
  };
  const validationSchema = Yup.object().shape({
    copyType: Yup.string().required(),
    selectedMembers: Yup.array()
      .required('Please select a team member')
      .min(1, 'Please select a team member'),
    copyFromDate: Yup.object().when('copyType', {
      is: 'custom',
      then: (schema) =>
        schema
          .shape({
            startDate: Yup.string(),
            endDate: Yup.string(),
          })
          .test(
            'required-dates',
            'Start date and end date are required',
            function (value) {
              if (!value) return false;
              return value.startDate && value.endDate;
            }
          ),
    }),
    copyToDate: Yup.object().when('copyType', {
      is: 'custom',
      then: (schema) =>
        schema
          .shape({
            startDate: Yup.string(),
            endDate: Yup.string(),
          })
          .test(
            'required-dates',
            'Start date and end date are required',
            function (value) {
              if (!value) return false;
              return value.startDate && value.endDate;
            }
          )
          .test(
            'date-compare',
            'Copy to date must be after copy from date range (both start and end dates)',
            function (value) {
              // eslint-disable-next-line react/no-this-in-sfc
              const { copyFromDate } = this.parent;
              if (
                !copyFromDate.startDate ||
                !copyFromDate.endDate ||
                !value?.startDate
              )
                return true;

              const copyToStart = new Date(value.startDate);
              const copyFromStart = new Date(copyFromDate.startDate);
              const copyFromEnd = new Date(copyFromDate.endDate);

              return copyToStart > copyFromStart && copyToStart > copyFromEnd;
            }
          )
          .test(
            'equal-date-range',
            'Selected date ranges must have equal number of days',
            function (value) {
              // eslint-disable-next-line react/no-this-in-sfc
              const { copyFromDate } = this.parent;
              if (
                !copyFromDate.startDate ||
                !copyFromDate.endDate ||
                !value?.startDate ||
                !value?.endDate
              )
                return true;

              const copyToStart = new Date(value.startDate);
              const copyToEnd = new Date(value.endDate);
              const copyFromStart = new Date(copyFromDate.startDate);
              const copyFromEnd = new Date(copyFromDate.endDate);

              const copyFromDays = Math.floor(
                (copyFromEnd.getTime() - copyFromStart.getTime()) /
                  (1000 * 60 * 60 * 24)
              );
              const copyToDays = Math.floor(
                (copyToEnd.getTime() - copyToStart.getTime()) /
                  (1000 * 60 * 60 * 24)
              );

              return copyFromDays === copyToDays;
            }
          ),
    }),
  });

  // Clear the team member field when project changes
  const clearTeamMemberFiled = () => {
    if (!selectCopyMembersInputRef.current) return;
    selectCopyMembersInputRef.current.select.clearValue();
  };
  const [selectedTeamMember, setSelectedTeamMember] = useState([]);

  const handleTeamMembers = (values) => {
    setSelectedTeamMember(values);
  };
  return (
    <Modal
      show={showCopySchedules}
      onHide={() => {
        handleShowCopySchedules(false);
      }}
      centered
    >
      <Modal.Header>
        <Modal.Title className="m-0">Copy Schedule</Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={() => {
            handleShowCopySchedules(false);
          }}
        />
      </Modal.Header>
      <Formik
        initialValues={{
          copyType: 'prevWeek',
          projectId:
            selectedProject?.value !== 'all' ? selectedProject?.value : '',
          selectedMembers: selectedTeamMembers,
          copyFromDate: {
            startDate: '',
            endDate: '',
          },
          copyToDate: {
            startDate: '',
            endDate: '',
          },
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const formikValues = { ...values };
          if (formikValues.selectedMembers.includes('all')) {
            formikValues.selectedMembers = selectedTeamMember;
          }
          handleCopySchedules(formikValues);
        }}
      >
        {(formikprops) => (
          <Form onSubmit={formikprops.handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-4">
                <Row>
                  <Col>
                    <Form.Check
                      type="radio"
                      id="prevWeek"
                      label="Copy from previous week"
                      name="copyType"
                      value="prevWeek"
                      checked={formikprops.values.copyType === 'prevWeek'}
                      onChange={() =>
                        formikprops.setFieldValue('copyType', 'prevWeek')
                      }
                    />
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      id="custom"
                      label="Custom date range (Max 7 days)"
                      name="copyType"
                      value="custom"
                      checked={formikprops.values.copyType === 'custom'}
                      onChange={() =>
                        formikprops.setFieldValue('copyType', 'custom')
                      }
                    />
                  </Col>
                </Row>
              </Form.Group>
              <ProjectListDropdown
                fieldName="projectId"
                projectsList={projectsList}
                handleChange={formikprops.handleChange}
                handleProjectChange={handleProjectChange}
                isDependedField
                otherActions={clearTeamMemberFiled}
              />
              <MemberMultiSelectDropdown
                id="selectCopyMembersInputRef"
                name="selectedMembers"
                labelText="Team Member(s)"
                fromArray={sortedMembersList}
                formProps={formikprops}
                isDisabled={sortedMembersList.length < 1 && true}
                innerRef={selectCopyMembersInputRef}
                isAllTeamMemberOption
                isAllTeamMemberValuesWithNoArray
                requiredValueAs="userId"
                showErrorMessage
                isRequiredField
                onChangeAction={handleTeamMembers}
              />
              {formikprops.values.copyType === 'custom' ? (
                <section className="mt-5">
                  <Form.Group>
                    <Form.Label>
                      Copy From <span className="text-danger">*</span>
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <DateRangePicker
                        onApply={(event, picker) => {
                          handleFromDateChange(event, picker);
                          formikprops.setFieldValue('copyFromDate', {
                            startDate: picker.startDate,
                            endDate: picker.endDate,
                          });
                        }}
                        initialSettings={{
                          autoUpdateInput: false,
                          locale: {
                            format: 'MMM D, YYYY',
                          },
                          alwaysShowCalendars: true,
                          autoApply: true,
                          maxSpan: {
                            days: 6,
                          },
                        }}
                      >
                        <input
                          type="text"
                          className="form-control icon-calendar"
                          value={
                            copyFromStartDate &&
                            copyFromEndDate &&
                            `${moment(copyFromStartDate).format(
                              'MMM D, YYYY'
                            )} - ${moment(copyFromEndDate).format(
                              'MMM D, YYYY'
                            )}`
                          }
                        />
                      </DateRangePicker>
                      <ErrorMessage
                        name="copyFromDate"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </div>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>
                      Copy To <span className="text-danger">*</span>
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <DateRangePicker
                        onApply={(event, picker) => {
                          handleToDateChange(event, picker);
                          formikprops.setFieldValue('copyToDate', {
                            startDate: picker.startDate,
                            endDate: picker.endDate,
                          });
                        }}
                        initialSettings={{
                          autoUpdateInput: false,
                          locale: {
                            format: 'MMM D, YYYY',
                          },
                          alwaysShowCalendars: true,
                          autoApply: true,
                          maxSpan: {
                            days: 6,
                          },
                        }}
                      >
                        <input
                          type="text"
                          className="form-control icon-calendar"
                          value={
                            copyToStartDate &&
                            copyToEndDate &&
                            `${moment(copyToStartDate).format(
                              'MMM D, YYYY'
                            )} - ${moment(copyToEndDate).format('MMM D, YYYY')}`
                          }
                        />
                      </DateRangePicker>
                      <ErrorMessage
                        name="copyToDate"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </div>
                  </Form.Group>
                </section>
              ) : (
                <section className="my-5">
                  <p>
                    Copying{' '}
                    <strong>{`${moment(selectedWeek.fromDate)
                      .subtract(7, 'days')
                      .format('MMM D')} - ${moment(selectedWeek.toDate)
                      .subtract(7, 'days')
                      .format('D, YYYY')}`}</strong>{' '}
                    schedules to{' '}
                    <strong>{`${moment(selectedWeek.fromDate).format(
                      'MMM D'
                    )} - ${moment(selectedWeek.toDate).format(
                      'D, YYYY'
                    )}`}</strong>
                  </p>
                </section>
              )}

              <Alert variant="light" className="mb-0">
                ⚠️ <strong>Please Note</strong>: Existing schedules for all
                selected team members on the destination dates will be{' '}
                overwritten.
              </Alert>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <Button
                variant="link"
                onClick={() => {
                  handleShowCopySchedules(false);
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Copy Schedules
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

CopyScheduleModal.propTypes = {
  showCopySchedules: PropTypes.bool,
  handleShowCopySchedules: PropTypes.func,
  handleCopySchedules: PropTypes.func,
  selectedWeek: PropTypes.object,
  projectsList: PropTypes.array,
  selectedProject: PropTypes.object,
  handleProjectChange: PropTypes.func,
  teamMembersList: PropTypes.array,
};

export default CopyScheduleModal;
